import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { useUser } from "../context/UserContext";
import { getUserAnswers, getQuestions, submitAnswer } from "../lib/api";
import { CategoryBadge } from "../components/CategoryBadge";
import { ANSWERS } from "../config";
import PageWrapper from "./PageWrapper";

const optionsFor = (question) =>
  question?.format === "CHOICE" ? (question.options || []) : ANSWERS;

export default function MyAnswersView() {
  const { currentUserId } = useUser();
  const [rows, setRows] = useState(null);
  const [savingId, setSavingId] = useState(null);

  const load = useCallback(async () => {
    if (!currentUserId) return;
    const [answers, questions] = await Promise.all([
      getUserAnswers(currentUserId),
      getQuestions(),
    ]);
    const qMap = Object.fromEntries(questions.map((q) => [q.id, q]));
    const merged = answers
      .map((a) => ({ ...a, question: qMap[a.questionId] }))
      .filter((r) => r.question);
    setRows(merged);
  }, [currentUserId]);

  useEffect(() => {
    load();
  }, [load]);

  const change = async (row, value) => {
    if (value === row.answer) return;
    setSavingId(row.questionId);
    try {
      await submitAnswer({ userId: currentUserId, questionId: row.questionId, answer: value });
      setRows((prev) =>
        prev.map((r) => (r.questionId === row.questionId ? { ...r, answer: value } : r))
      );
      toast.success("Réponse mise à jour");
    } catch (e) {
      toast.error("Erreur lors de la mise à jour");
    } finally {
      setSavingId(null);
    }
  };

  return (
    <PageWrapper>
      <div className="answers-view" data-testid="my-answers-view">
        <p className="page-eyebrow">Confidentiel</p>
        <h1 className="page-title">Mes réponses</h1>
        <p className="page-note">Vous seul·e voyez vos réponses. Vous pouvez les modifier.</p>

        {rows === null ? (
          <div className="center-note">Chargement…</div>
        ) : rows.length === 0 ? (
          <div className="empty-state">
            <p className="empty-text">Vous n'avez encore répondu à aucune carte.</p>
          </div>
        ) : (
          <div className="answers-list">
            {rows.map((row) => {
              const isChoice = row.question.format === "CHOICE";
              return (
                <div key={row.questionId} className="answer-item" data-testid="answer-item">
                  <div className="answer-item-head">
                    <CategoryBadge category={row.question.category} size="sm" />
                  </div>
                  <p className="answer-item-text">{row.question.text}</p>
                  <div
                    className="answer-item-controls"
                    style={isChoice ? { flexDirection: "column" } : undefined}
                  >
                    {optionsFor(row.question).map((a) => (
                      <button
                        key={a.value}
                        data-testid={`edit-answer-${a.value}`}
                        disabled={savingId === row.questionId}
                        className={`mini-answer ${isChoice ? "" : `mini-${a.value}`} ${row.answer === a.value ? "active" : ""}`}
                        style={isChoice ? { width: "100%" } : undefined}
                        onClick={() => change(row, a.value)}
                      >
                        {a.emoji && <span>{a.emoji}</span>}
                        {a.label}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </PageWrapper>
  );
}
